import "../../utils/tailwind.css";
import { PageProvider, useCoachCoursesProvider } from "./provider";
import Label from "../../components/label";
import Section from "../../components/section";
import Trash from "../../components/svg/trash";
import Input from "../../components/input";
import Button from "../../components/Buttons";
import { Eye, X, Paperclip, BookOpen } from "lucide-react";
import Const from "../../utils/constant";

const formatDuration = (dur) => {
  if (!dur) return "N/A";
  if (typeof dur === "string" || typeof dur === "number") return String(dur);
  if (typeof dur === "object") {
    const hours = dur.hours ? `${dur.hours} hr${dur.hours > 1 ? "s" : ""}` : "";
    const minutes = dur.minutes ? `${dur.minutes} min${dur.minutes > 1 ? "s" : ""}` : "";
    const formatted = [hours, minutes].filter(Boolean).join(" ");
    return formatted || JSON.stringify(dur);
  }
  return String(dur);
};

const renderRichContent = (text, className = "") => {
  if (!text) return null;
  const contentStr = typeof text === "object" ? JSON.stringify(text, null, 2) : String(text);
  const isHtml = /<[a-z][\s\S]*>/i.test(contentStr);

  if (isHtml) {
    return (
      <div
        className={`rich-text-content leading-relaxed text-gray-700 ${className}`}
        dangerouslySetInnerHTML={{ __html: contentStr }}
      />
    );
  }

  return <div className={`whitespace-pre-line text-gray-700 ${className}`}>{contentStr}</div>;
};

export default function CoachCourses() {
  return (
    <div className="p-3">
      <PageProvider>
        <PageContent />
      </PageProvider>
    </div>
  );
}

function PageContent() {
  const {
    getCoursesListQuery,
    currentPage,
    handlePageChange,
    handleOnChange,
    isDetailsModalOpen,
    selectedCourseForDetails,
    isCourseDetailsLoading,
    handleOpenDetailsModal,
    handleCloseDetailsModal,
    isDeleteModalOpen,
    selectedCourseForDelete,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleConfirmDelete,
    isDeleting,
  } = useCoachCoursesProvider();

  const coursesList = getCoursesListQuery?.data?.data?.data || [];
  const paging = getCoursesListQuery?.data?.data?.paging || {};

  return (
    <Section className="mt-6 pt-2">
      <div>
        {/* Header & Search */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 mt-2">
          <Label className="mb-0 text-black text-[20px] font-bold" label="Coach Courses List" />
          <Input
            showSearchIcon={true}
            onChange={handleOnChange}
            placeholder="Search course title or coach..."
            rootClassName="!w-full sm:!w-[340px]"
          />
        </div>

        {/* Table Container */}
        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse border border-gray-300">
            <thead className="bg-gray-200">
              <tr>
                <th className="border font-semibold border-gray-300 px-3 py-2 text-left text-[14px] w-[50px]">Sr.No</th>
                <th className="border font-semibold border-gray-300 px-3 py-2 text-left text-[14px]">Course Title</th>
                <th className="border font-semibold border-gray-300 px-3 py-2 text-left text-[14px]">Coach Account</th>
                <th className="border font-semibold border-gray-300 px-3 py-2 text-left text-[14px] w-[120px]">Access / Price</th>
                <th className="border font-semibold border-gray-300 px-3 py-2 text-left text-[14px] w-[100px]">Status</th>
                <th className="border font-semibold border-gray-300 px-3 py-2 text-left text-[14px] w-[130px]">Created Date</th>
                <th className="border font-semibold border-gray-300 px-3 py-2 text-center text-[14px] w-[100px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getCoursesListQuery?.isPending ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="border border-gray-300 px-3 py-3 text-center">
                      <div className="h-4 bg-gray-200 rounded w-6 mx-auto"></div>
                    </td>
                    <td className="border border-gray-300 px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded w-48"></div>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-3 py-3">
                      <div className="space-y-1.5">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                        <div className="h-3 bg-gray-200 rounded w-40"></div>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-3 py-3">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="border border-gray-300 px-3 py-3">
                      <div className="h-5 bg-gray-200 rounded-full w-20"></div>
                    </td>
                    <td className="border border-gray-300 px-3 py-3">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                    </td>
                    <td className="border border-gray-300 px-3 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        <div className="w-5 h-5 bg-gray-200 rounded"></div>
                        <div className="w-5 h-5 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : coursesList.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-6 text-gray-500">
                    No courses found.
                  </td>
                </tr>
              ) : (
                coursesList.map((item, index) => {
                  const coach = item?.user || {};
                  const coachName = coach.full_name || "N/A";
                  const coachEmail = coach.email || "";
                  const priceLabel = item?.payment_details?.price
                    ? `₹${item.payment_details.price}`
                    : item?.access_type === "free"
                      ? "Free"
                      : item?.access_type || "N/A";
                  const createdDate = item?.created_at
                    ? new Date(item.created_at).toLocaleDateString()
                    : "N/A";

                  return (
                    <tr key={item.id || index} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-3 py-2 text-center text-[14px]">
                        {(currentPage - 1) * 10 + index + 1}
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        <div className="flex items-center gap-2">
                          {item.cover_url && (
                            <img
                              src={Const.getS3File(item.cover_url)}
                              alt={item.title}
                              className="w-8 h-8 rounded object-cover border"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                          <span className="font-medium text-black text-[14px]">{item.title || "Untitled Course"}</span>
                        </div>
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        <div className="flex flex-col">
                          <span className="font-semibold text-black text-[14px]">{coachName}</span>
                          {coachEmail && <span className="text-xs text-gray-500">{coachEmail}</span>}
                        </div>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-[14px]">
                        <span className="capitalize">{priceLabel}</span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-[14px]">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-semibold ${item.is_published
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                            }`}
                        >
                          {item.is_published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-[14px] text-gray-600">
                        {createdDate}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            title="View Course Details"
                            onClick={() => handleOpenDetailsModal(item)}
                            className="text-blue-600 hover:text-blue-800 p-1 transition-colors"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            title="Delete Course"
                            onClick={() => handleOpenDeleteModal(item)}
                            className="text-red-600 hover:text-red-800 p-1 transition-colors"
                          >
                            <Trash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {paging.total_pages > 1 && (
            <div className="flex justify-end mt-4 pr-4">
              <div className="flex gap-2">
                {Array.from({ length: paging.total_pages }, (_, i) => i + 1).map((pageNo) => (
                  <button
                    key={pageNo}
                    onClick={() => handlePageChange(pageNo)}
                    className={`px-3 py-1 border rounded ${currentPage === pageNo ? "bg-black text-white" : "bg-white text-black"
                      }`}
                  >
                    {pageNo}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Course Details Modal */}
      {isDetailsModalOpen && selectedCourseForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999] p-4">
          <div className="bg-white rounded-xl max-w-5xl w-[92vw] max-h-[90vh] overflow-y-auto p-6 shadow-2xl relative">
            <button
              onClick={handleCloseDetailsModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>

            <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Course & Coach Details</h3>

            <div className="space-y-4">
              {/* Cover Image */}
              {selectedCourseForDetails.cover_url && (
                <div className="w-full h-64 rounded-lg overflow-hidden border bg-gray-100 relative">
                  <img
                    src={Const.getS3File(selectedCourseForDetails.cover_url)}
                    alt={selectedCourseForDetails.title || "Course Cover"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}

              {/* Course Title & Status */}
              <div>
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-xl font-bold text-gray-900">{selectedCourseForDetails.title}</h4>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${selectedCourseForDetails.is_published
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                      }`}
                  >
                    {selectedCourseForDetails.is_published ? "Published" : "Draft"}
                  </span>
                </div>
                {selectedCourseForDetails.slug_url && (
                  <p className="text-xs text-gray-400 font-mono mt-1">Slug: {selectedCourseForDetails.slug_url}</p>
                )}
              </div>

              {/* Coach Account Section */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                <h5 className="text-sm font-bold text-purple-900 uppercase tracking-wider mb-2">Coach Account Details</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500 font-medium block text-xs">Coach Name</span>
                    <span className="font-semibold text-gray-800">{selectedCourseForDetails.user?.full_name || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 font-medium block text-xs">Email</span>
                    <span className="font-semibold text-gray-800">{selectedCourseForDetails.user?.email || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 font-medium block text-xs">Mobile</span>
                    <span className="font-semibold text-gray-800">{selectedCourseForDetails.user?.mobile_number || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 font-medium block text-xs">Role</span>
                    <span className="font-semibold text-gray-800 capitalize">{selectedCourseForDetails.user?.role || "coach"}</span>
                  </div>
                </div>
              </div>

              {/* Course Meta Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm bg-gray-50 p-4 rounded-lg border">
                <div>
                  <span className="text-gray-500 font-medium block text-xs">Access Type</span>
                  <span className="font-semibold text-gray-800 capitalize">{selectedCourseForDetails.access_type || "N/A"}</span>
                </div>
                <div>
                  <span className="text-gray-500 font-medium block text-xs">Price</span>
                  <span className="font-semibold text-gray-800">
                    {selectedCourseForDetails.payment_details?.price
                      ? `₹${selectedCourseForDetails.payment_details.price}`
                      : selectedCourseForDetails.access_type === "free"
                        ? "Free"
                        : "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 font-medium block text-xs">Duration</span>
                  <span className="font-semibold text-gray-800">{formatDuration(selectedCourseForDetails.duration)}</span>
                </div>
                <div>
                  <span className="text-gray-500 font-medium block text-xs">Created On</span>
                  <span className="font-semibold text-gray-800">
                    {selectedCourseForDetails.created_at
                      ? new Date(selectedCourseForDetails.created_at).toLocaleString()
                      : "N/A"}
                  </span>
                </div>
              </div>

              {/* Course Description */}
              {selectedCourseForDetails.description && (
                <div>
                  <h5 className="text-sm font-bold text-gray-700 mb-1">Course Description</h5>
                  <div className="bg-gray-50 p-4 rounded-lg border">
                    {renderRichContent(selectedCourseForDetails.description)}
                  </div>
                </div>
              )}

              {/* Course Modules & Content Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-base font-bold text-gray-800 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-purple-700" />
                    Course Modules & Content
                  </h5>
                  <span className="text-xs text-gray-500 font-medium">
                    {selectedCourseForDetails.modules?.length || 0} Module(s)
                  </span>
                </div>

                {isCourseDetailsLoading ? (
                  <div className="py-6 text-center text-sm text-gray-500 font-medium">
                    Loading course modules and content...
                  </div>
                ) : !selectedCourseForDetails.modules || selectedCourseForDetails.modules.length === 0 ? (
                  <div className="bg-gray-50 border rounded-lg p-4 text-center text-sm text-gray-500">
                    No modules or content found for this course.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedCourseForDetails.modules.map((module, idx) => {
                      const contents = module.module_contents || module.module_content || [];

                      return (
                        <div key={module.id || idx} className="border rounded-lg overflow-hidden bg-white shadow-sm">
                          <div className="bg-purple-50/70 border-b px-4 py-3 flex items-center justify-between">
                            <div>
                              <span className="text-xs font-bold uppercase tracking-wider text-purple-700 block">
                                Module {module.sequence_no || idx + 1}
                              </span>
                              <h6 className="text-sm font-bold text-gray-900">{module.title || "Untitled Module"}</h6>
                              {module.description && (
                                <div className="text-xs text-gray-600 mt-0.5">
                                  {renderRichContent(module.description)}
                                </div>
                              )}
                            </div>
                            <span className="text-xs bg-purple-100 text-purple-800 font-semibold px-2.5 py-1 rounded-full">
                              {contents.length} item(s)
                            </span>
                          </div>

                          <div className="p-3 space-y-3 bg-gray-50/50">
                            {contents.length === 0 ? (
                              <p className="text-xs text-gray-400 italic px-1 py-1">No content items in this module.</p>
                            ) : (
                              contents.map((content, cIdx) => {
                                const attachments = content.module_content_attachments || [];

                                return (
                                  <div
                                    key={content.id || cIdx}
                                    className="p-3 border rounded-lg bg-white shadow-xs hover:border-purple-200 transition-colors"
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="w-full">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-semibold text-gray-900 text-sm">
                                            {cIdx + 1}. {content.title || "Untitled Lesson"}
                                          </span>
                                          <span
                                            className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${content.type === "video"
                                              ? "bg-blue-100 text-blue-800"
                                              : content.type === "quiz"
                                                ? "bg-amber-100 text-amber-800"
                                                : content.type === "assignment"
                                                  ? "bg-purple-100 text-purple-800"
                                                  : "bg-gray-100 text-gray-800"
                                              }`}
                                          >
                                            {content.type || "lesson"}
                                          </span>
                                        </div>
                                        {content.objective && (
                                          <div className="mt-1 bg-gray-50 p-2.5 rounded border border-gray-200">
                                            <span className="font-semibold text-xs text-gray-700 block mb-0.5">Objective:</span>
                                            {renderRichContent(content.objective, "text-xs")}
                                          </div>
                                        )}
                                        {content.description && (
                                          <div className="mt-1 bg-gray-50 p-2.5 rounded border border-gray-200">
                                            <span className="font-semibold text-xs text-gray-700 block mb-0.5">Details:</span>
                                            {renderRichContent(content.description, "text-xs")}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Video / File Attachments Player */}
                                    {attachments.length > 0 && (
                                      <div className="mt-3 pt-2 border-t border-gray-100 space-y-2">
                                        <span className="text-xs font-semibold text-gray-700 block">
                                          Media & Attachments:
                                        </span>
                                        {attachments.map((att, aIdx) => {
                                          const fileUrl = Const.getS3File(att.file_url);
                                          const isVideo =
                                            att.file_type?.includes("video") ||
                                            att.file_url?.match(/\.(mp4|webm|mov|mkv)$/i) ||
                                            content.type === "video";

                                          return (
                                            <div key={att.id || aIdx} className="space-y-1">
                                              {isVideo && (
                                                <div className="mt-1">
                                                  <video
                                                    src={fileUrl}
                                                    controls
                                                    className="w-full max-h-56 rounded border bg-black"
                                                    preload="metadata"
                                                  />
                                                </div>
                                              )}
                                              <a
                                                href={fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 text-xs text-purple-700 hover:text-purple-900 font-medium underline"
                                              >
                                                <Paperclip className="w-3.5 h-3.5" />
                                                View / Download File ({att.file_type || "Attachment"})
                                              </a>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {/* Quiz Questions Preview */}
                                    {content.type === "quiz" &&
                                      content.quiz_data &&
                                      Array.isArray(content.quiz_data.questions) && (
                                        <div className="mt-3 pt-2 border-t border-amber-200 bg-amber-50/70 p-3 rounded-md">
                                          <span className="text-xs font-bold text-amber-900 block mb-1">
                                            Quiz Questions ({content.quiz_data.questions.length}):
                                          </span>
                                          <ol className="list-decimal list-inside text-xs space-y-1.5 text-amber-900">
                                            {content.quiz_data.questions.map((q, qIdx) => (
                                              <li key={qIdx} className="font-medium">
                                                {q.question}
                                                {q.options && (
                                                  <ul className="list-disc list-inside ml-4 text-[11px] text-amber-800 font-normal mt-0.5">
                                                    {q.options.map((opt, oIdx) => (
                                                      <li key={oIdx}>{opt}</li>
                                                    ))}
                                                  </ul>
                                                )}
                                              </li>
                                            ))}
                                          </ol>
                                        </div>
                                      )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={handleCloseDetailsModal} className="!bg-[#7C13CA] text-white">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedCourseForDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-[360px] text-center shadow-xl">
            <h4 className="text-lg font-bold text-gray-800 mb-2">Delete Course</h4>
            <p className="text-sm text-gray-600 mb-2">
              Are you sure you want to delete <span className="font-bold text-black">"{selectedCourseForDelete.title}"</span>?
            </p>
            <p className="text-xs text-gray-500 mb-6">
              Coach: {selectedCourseForDelete.user?.full_name || selectedCourseForDelete.user?.email || "N/A"}
            </p>

            <div className="flex justify-center gap-4">
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-semibold text-sm transition-colors disabled:opacity-50"
              >
                {isDeleting ? "Deleting..." : "Yes, Delete"}
              </button>
              <button
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded font-semibold text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}
